import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CalendarIcon, 
  ChartBarIcon, 
  ClockIcon, 
  SparklesIcon,
  ArrowTopRightOnSquareIcon,
  VideoCameraIcon,
  ChartPieIcon,
  AcademicCapIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import demoImg from '../assets/demo.png';
import golfDemoImg from '../assets/golf-demo.png';

// Create custom golf icon since Heroicons doesn't have one
const GolfIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="20" 
    height="20" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="1.5" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className="w-5 h-5"
  >
    <circle cx="12" cy="8" r="7" />
    <path d="M12 15v7" />
    <path d="M10 22h4" />
    <path d="m7 8 10-2" />
  </svg>
);

const Feature = ({ icon: Icon, title, description }: { icon: any; title: string; description: string }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    viewport={{ once: true }}
    className="flex items-start gap-4 p-4 rounded-xl bg-white/50 backdrop-blur-sm border border-primary/10 hover:border-[#FF7066]/20 transition-all duration-300"
  >
    <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-[#FF7066]/10 transition-colors duration-300">
      <Icon className="w-6 h-6 text-primary group-hover:text-[#FF7066] transition-colors duration-300" />
    </div>
    <div>
      <h4 className="font-semibold text-primary mb-1 group-hover:text-[#FF7066] transition-colors duration-300">{title}</h4>
      <p className="text-secondary/80 text-sm">{description}</p>
    </div>
  </motion.div>
);

// Fix the TabButton component to handle nested content properly
const TabButton = ({ 
  active, 
  onClick, 
  children, 
  icon: Icon 
}: { 
  active: boolean; 
  onClick: () => void; 
  children: React.ReactNode;
  icon?: React.ElementType;  
}) => (
  <motion.button
    onClick={onClick} // This should capture clicks on the entire button
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.98 }}
    className={`px-6 py-3 font-medium rounded-lg transition-all duration-300 flex items-center gap-2 min-w-[160px] justify-center ${
      active 
        ? 'bg-[#FF7066] text-white shadow-md' 
        : 'bg-white/50 text-secondary hover:bg-white hover:text-primary border border-gray-200'
    }`}
  >
    {Icon && <Icon className="w-5 h-5" />}
    {children}
  </motion.button>
);

const FeaturedApp = () => {
  const [activeTab, setActiveTab] = useState<'scheduli' | 'golf'>('scheduli');
  
  const scheduliFeatures = [
    {
      icon: SparklesIcon,
      title: "AI-Powered Planning",
      description: "Advanced LLM technology that understands your business context and optimises scheduling decisions."
    },
    {
      icon: ChartBarIcon,
      title: "Smart Optimisation",
      description: "Automatically increases efficiency by analysing patterns and suggesting optimal time slots."
    },
    {
      icon: CalendarIcon,
      title: "Seamless Integration",
      description: "Works with your existing calendar systems and business tools for a smooth workflow."
    },
    {
      icon: ClockIcon,
      title: "Time-Saving Automation",
      description: "Reduces manual scheduling time by up to 80% with intelligent automation."
    }
  ];
  
  const golfFeatures = [
    {
      icon: VideoCameraIcon,
      title: "Vision Analysis",
      description: "Advanced computer vision models detect and analyze every aspect of your golf swing technique."
    },
    {
      icon: ChartPieIcon,
      title: "Pose Data Extraction",
      description: "Precise extraction of body position data at every point in your swing for comprehensive feedback."
    },
    {
      icon: AcademicCapIcon,
      title: "Expert LLM Coaching",
      description: "Personalized coaching insights from an LLM trained on thousands of professional swings."
    },
    {
      icon: ArrowPathIcon,
      title: "Continuous Improvement",
      description: "Track your progress over time with detailed metrics and actionable improvement plans."
    }
  ];

  const features = activeTab === 'scheduli' ? scheduliFeatures : golfFeatures;
  const appTitle = activeTab === 'scheduli' ? 'Scheduli AI' : 'GolfAI Guru';
  const appDescription = activeTab === 'scheduli' 
    ? 'A revolutionary AI-powered scheduling solution that transforms how businesses manage their time and resources.'
    : 'Professional-level golf swing analysis powered by advanced AI vision models and personalized coaching feedback.';
  const demoImage = activeTab === 'scheduli' ? demoImg : golfDemoImg;
  const appUrl = activeTab === 'scheduli' ? 'https://scheduli-ai.com' : 'https://golfaiguru.com';
  const appCta = activeTab === 'scheduli' ? 'Visit Scheduli AI' : 'Try GolfAI Guru';
  const appPreviewTitle = activeTab === 'scheduli' ? 'Experience the Future of Scheduling' : 'Elevate Your Golf Game';
  const appPreviewDesc = activeTab === 'scheduli' 
    ? 'Built with cutting-edge AI technology, Scheduli AI revolutionises how businesses handle their scheduling needs. Our bespoke solution combines powerful LLM capabilities with intuitive design to deliver an unmatched scheduling experience.'
    : 'GolfAI Guru uses state-of-the-art vision AI to analyze your golf swing from any video. Get instant feedback on your posture, swing plane, timing, and follow-through with personalized tips from our AI coach trained on professional swings.';

  return (
    <section id="featured-app" className="py-24 relative overflow-hidden bg-gradient-to-b from-white via-[#FF7066]/5 to-white">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.03]" />
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(255, 112, 102, 0.03), transparent 70%)',
        }}
      />
      
      {/* Floating Orbs */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          repeatType: "reverse",
        }}
        className="absolute top-20 right-20 w-64 h-64 bg-[#FF7066]/10 rounded-full blur-3xl"
      />
      
      <div className="container relative z-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-[#FF7066]/10 to-secondary/10 text-[#FF7066] text-sm font-medium border border-[#FF7066]/10 mb-6"
            >
              Featured Solutions
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-primary mb-6"
            >
              Meet <span className="bg-gradient-to-r from-[#FF7066] to-secondary bg-clip-text text-transparent">{appTitle}</span>
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-xl text-secondary mb-8"
            >
              {appDescription}
            </motion.p>
            
            {/* Fixed Tab Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
              <TabButton 
                active={activeTab === 'scheduli'}
                onClick={() => setActiveTab('scheduli')}
                icon={CalendarIcon}
              >
                Scheduli AI
              </TabButton>
              
              {/* Simplified Golf Tab structure for better click handling */}
              <motion.div
                animate={activeTab !== 'golf' ? {
                  y: [0, -4, 0],
                  scale: [1, 1.05, 1]
                } : {}}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
                className="relative"
                onClick={() => setActiveTab('golf')} // Add click handler to the wrapper too
              >
                <TabButton 
                  active={activeTab === 'golf'}
                  onClick={() => setActiveTab('golf')}
                  icon={GolfIcon}
                >
                  GolfAI Guru
                  {activeTab !== 'golf' && (
                    <span className="ml-1 inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                      New
                    </span>
                  )}
                </TabButton>
                {activeTab !== 'golf' && (
                  <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 to-pink-500 opacity-30 blur rounded-lg pointer-events-none"></div>
                )}
              </motion.div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div key={feature.title} className="group">
                  <Feature
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.description}
                  />
                </div>
              ))}
            </div>

            {/* App Preview Section */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#FF7066]/10 via-secondary/10 to-transparent rounded-3xl" />
              <div className="relative bg-white/50 backdrop-blur-sm rounded-3xl border border-[#FF7066]/10 p-8 hover:shadow-xl hover:shadow-[#FF7066]/5 transition-all duration-300">
                <div className="aspect-video bg-[#FF7066]/5 rounded-2xl mb-6 flex items-center justify-center">
                  <img src={demoImage} alt={`${appTitle} Preview`} className="object-cover rounded-2xl" />
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-primary">{appPreviewTitle}</h3>
                  <p className="text-secondary">
                    {appPreviewDesc}
                  </p>
                  
                  <a
                    href={appUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#FF7066] text-white hover:bg-[#FF8A82] transition-colors duration-300"
                  >
                    {appCta}
                    <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedApp;
